import React, { ReactNode, createContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import useApiAuth from '@admission/apis/auth/useApiAuth';
import { User } from '@admission/apis/auth/types';

import { useSH } from '@libs/creditkit';

import { useWebBridgeContext } from '@libs/app-bridge';

import { alovaInstanceAuth } from '@admission/apis/auth/alovaInstanceOnboarding';

import { useAuthentication } from '@libs/acp/contexts/Authentication';
import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';

import { useUserStore } from '@libs/acp/store/user/useUserStore';
import { queryClient } from '@common/services/queryClient';
import { useNavigate } from 'react-router-dom';
import { RouteMap } from '@common/constants/RouteMap';

interface IUserContext {
  user: User | null;
  updateUser: (user: User) => void;
  signOut: (isLogOut?: boolean) => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  getTokens: () => { accessToken: string; refreshToken: string };
}

interface IUserProvider {
  children: ReactNode;
}

const UserContext = createContext<IUserContext>({} as IUserContext);

const UserProvider: React.FC<IUserProvider> = ({ children }) => {
  const { isAuthenticated, isLoading, customer } = useAuthentication();
  const [user, setUser] = useState(customer as User | null);

  // Hooks
  const { bridge, notifications } = useWebBridgeContext();
  const { apiAuthLogout } = useApiAuth();
  const { resetContext } = useSH();
  const { acpLogout } = useAuthenticatedUser();
  const navigate = useNavigate();

  useEffect(() => {
    setUser((prev) => ({ ...prev, ...customer } as User));
  }, [customer]);

  useEffect(() => {
    if (user?.email) notifications.applyPushOnEngage(user.email, isAuthenticated);
  }, [user?.email, isAuthenticated]);

  // This is used to track the user on the app
  // and to send the user id to the webview
  useEffect(() => {
    if (!bridge.isWebViewBridgeAvailable) return;

    (async () => {
      if (isLoading) return;

      if (!isAuthenticated) {
        await Promise.all([bridge.appsFlyerSetCustomerUserId(''), bridge.analyticsFacebookSetUserId(''), bridge.analyticsFirebaseSetUserId('')]);
        return;
      }

      const user = await getUser();
      if (!user) return;

      await Promise.all([
        bridge.appsFlyerSetCustomerUserId(user.uuid),
        bridge.analyticsFacebookSetUserId(user.uuid),
        bridge.analyticsFirebaseSetUserId(user.uuid),
      ]);
    })();
  }, [isLoading, isAuthenticated]);

  useEffect(() => {
    window.addEventListener('handleSignOut', signOut);

    return () => {
      window.removeEventListener('handleSignOut', signOut);
    };
  }, []);

  async function signOut() {
    // Navigate to login
    navigate(RouteMap.SignIn, { replace: true, state: { from: RouteMap.Logout } });

    // Fire and forget logout request
    apiAuthLogout.send();

    // Fire and forget remove push notification tokens
    const storedAppPushPayload = localStorage.getItem('app_pn');
    if (storedAppPushPayload) {
      const pushNotificationsPayload = JSON.parse(storedAppPushPayload);
      const updateAppPushNotification = alovaInstanceAuth.Post('/v2/app/update-app-push-notification', {
        app_version: pushNotificationsPayload.version,
        expo_token: 'user logged out',
        android_token: 'user logged out',
        ios_token: 'user logged out',
      });

      updateAppPushNotification.send();
    }

    const WHITELISTED_COOKIES = [
      { value: '_', rule: 'startsWith' },
      { value: 'rl_', rule: 'startsWith' },
      { value: 'dx-', rule: 'startsWith' },
      { value: 'start_source_url', rule: 'equals' },
    ] as const;

    // Delete all cookies except whitelisted ones
    Object.keys(Cookies.get()).forEach((cookieName) => {
      const cookieNameLower = cookieName.toLowerCase();
      const shouldKeep = WHITELISTED_COOKIES.some(({ value, rule }) => {
        switch (rule) {
          case 'startsWith':
            return cookieNameLower.startsWith(value);
          case 'equals':
            return cookieNameLower === value;
          default:
            return false;
        }
      });

      if (!shouldKeep) {
        const domain = window.location.hostname.split('.').slice(-3).join('.');
        Cookies.remove(cookieName, { domain: `.${domain}`, path: '/' });
        Cookies.remove(cookieName, { domain: window.location.hostname, path: '/' });
      }
    });

    // Clear ACP user data
    acpLogout();

    // Clear localStorage and sessionStorage
    localStorage.clear();
    sessionStorage.clear();

    // Clear indexedDB
    indexedDB.deleteDatabase('newco');

    // Clear sorting hat context
    resetContext();

    // Clear query client cache
    queryClient.clear();
  }

  async function updateUser(updateUser: User) {
    // Fazer um wrapper no update user do ACP
    const safeNewUser = { ...customer, ...updateUser };
    setUser(safeNewUser);
  }

  async function getUser() {
    return useUserStore.getState().authenticatedUser.acp.customer as User;
  }

  function getTokens() {
    const { access_token: accessToken, refresh_token: refreshToken } = useUserStore.getState().authenticatedUser.acp.auth;

    return {
      accessToken,
      refreshToken,
    };
  }

  return (
    <UserContext.Provider
      value={{
        user,
        getTokens,
        updateUser,
        signOut,
        isAuthenticated,
        isLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
export { UserProvider, UserContext };
